import { gapi } from "gapi-script";
import { initGoogleClient } from "./config";
import { authManagerInstance, AuthProvider } from "../values";

export const fetchGoogleDriveFiles = async (account, setLoading, setError) => {
    try {
        if (!account) {
            throw new Error("No account provided. Please sign in first.");
        }
        setLoading && setLoading(true);
        if (!window.gapi || !window.gapi.client || !window.gapi.client.drive) {
            await initGoogleClient();
            if (!window.gapi || !window.gapi.client || !window.gapi.client.drive) {
                throw new Error("Google Drive API client not initialized properly");
            }
        }
        await validateGoogleAccount(account, setError);
        let allFiles = [];
        let pageToken = null;
        do {
            try {
                const response = await gapi.client.drive.files.list({
                    pageSize: 100,
                    fields: 'nextPageToken, files(id, name, mimeType, modifiedTime, size, exportLinks, webViewLink, iconLink)',
                    pageToken: pageToken
                });

                allFiles = allFiles.concat(response.result.files);
                pageToken = response.result.nextPageToken;
            } catch (listError) {
                if (listError.status === 401 ||
                    (listError.result && listError.result.error &&
                        (listError.result.error.status === 'UNAUTHENTICATED' ||
                            listError.result.error.code === 401))) {
                    await initGoogleClient();
                    const response = await gapi.client.drive.files.list({
                        pageSize: 100,
                        fields: 'nextPageToken, files(id, name, mimeType, modifiedTime, size, exportLinks, webViewLink, iconLink)',
                        pageToken: pageToken,
                    });
                    allFiles = allFiles.concat(response.result.files);
                    pageToken = response.result.nextPageToken;
                } else {
                    throw listError;
                }
            }
        } while (pageToken);
        allFiles = allFiles.map(file => ({
            ...file,
            account: account,
            provider: AuthProvider.GOOGLE,
        }));
        return allFiles;
    } catch (error) {
        setError && setError(`Failed to fetch Google Drive files: ${error.message}`);
        return [];
    } finally {
        setLoading && setLoading(false);
    }
};

export const searchGoogleDriveFiles = async (query, setLoading, activeAccount, setFileList) => {
    try {
        setLoading(true);
        await initGoogleClient();
        if (!window.gapi || !window.gapi.client || !window.gapi.client.drive) {
            throw new Error("Google Drive API not loaded");
        }
        const response = await window.gapi.client.drive.files.list({
            q: `name contains '${query}' and trashed = false`,
            fields: 'files(id, name, mimeType, size, modifiedTime, webViewLink)',
            orderBy: 'modifiedTime desc',
        });
        const files = response.result.files.map(file => ({
            ...file,
            provider: AuthProvider.GOOGLE,
        }));

        setFileList(files || []);

    } catch (error) {
        throw error;
    } finally {
        setLoading(false);
    }
};

export const downloadGoogleDriveFile = async (fileId, account, setLoading, setError) => {
    try {
        if (!fileId) {
            throw new Error("File ID is required");
        }
        if (!account) {
            throw new Error("No account provided. Please sign in first.");
        }
        setLoading && setLoading(true);
        if (!window.gapi || !window.gapi.client || !window.gapi.client.drive) {
            await initGoogleClient();
            if (!window.gapi || !window.gapi.client || !window.gapi.client.drive) {
                throw new Error("Google Drive API client not initialized properly");
            }
        }
        await validateGoogleAccount(account, setError);
        try {
            const fileMetadata = await gapi.client.drive.files.get({
                fileId: fileId,
                fields: 'mimeType,name'
            });
            if (fileMetadata.result.mimeType.includes('application/vnd.google-apps.')) {
                let exportMimeType = 'application/pdf';
                if (fileMetadata.result.mimeType.includes('document')) {
                    exportMimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
                } else if (fileMetadata.result.mimeType.includes('spreadsheet')) {
                    exportMimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
                } else if (fileMetadata.result.mimeType.includes('presentation')) {
                    exportMimeType = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
                }
                const response = await gapi.client.drive.files.export({
                    fileId: fileId,
                    mimeType: exportMimeType,
                });
                return response.body;
            } else {
                const response = await gapi.client.drive.files.get({
                    fileId: fileId,
                    alt: 'media',
                });
                return response.body;
            }
        } catch (downloadError) {
            if (downloadError.status === 401 ||
                (downloadError.result && downloadError.result.error &&
                    (downloadError.result.error.status === 'UNAUTHENTICATED' ||
                        downloadError.result.error.code === 401))) {
                await initGoogleClient();
                const response = await gapi.client.drive.files.get({
                    fileId: fileId,
                    alt: 'media',
                });
                return response.body;
            } else {
                throw downloadError;
            }
        }
    } catch (error) {
        setError && setError(`Failed to download file: ${error.message}`);
        return null;
    } finally {
        setLoading && setLoading(false);
    }
};

export const createGoogleDriveFile = async (fileName, fileContent, mimeType, account, setLoading, setError) => {
    try {
        if (!fileName) {
            throw new Error("File name is required");
        }
        if (!account) {
            throw new Error("No account provided. Please sign in first.");
        }
        setLoading && setLoading(true);
        if (!window.gapi || !window.gapi.client || !window.gapi.client.drive) {
            await initGoogleClient();
            if (!window.gapi || !window.gapi.client || !window.gapi.client.drive) {
                throw new Error("Google Drive API client not initialized properly");
            }
        }
        await validateGoogleAccount(account, setError);
        const accessToken = gapi.auth.getToken().access_token;
        if (mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
            const metadata = {
                name: fileName,
                mimeType: 'application/vnd.google-apps.document',
            };
            const createRequest = new XMLHttpRequest();
            createRequest.open('POST', 'https://www.googleapis.com/drive/v3/files');
            createRequest.setRequestHeader('Authorization', 'Bearer ' + accessToken);
            createRequest.setRequestHeader('Content-Type', 'application/json');
            createRequest.responseType = 'json';
            return new Promise((resolve, reject) => {
                createRequest.onload = async () => {
                    if (createRequest.status === 200) {
                        const fileId = createRequest.response.id;
                        let htmlContent;
                        if (fileContent instanceof Blob) {
                            htmlContent = await fileContent.text();
                        } else if (typeof fileContent === 'string') {
                            htmlContent = fileContent;
                        } else {
                            htmlContent = '<p>Empty document</p>';
                        }
                        const updateRequest = new XMLHttpRequest();
                        updateRequest.open('PATCH', `https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media`);
                        updateRequest.setRequestHeader('Authorization', 'Bearer ' + accessToken);
                        updateRequest.setRequestHeader('Content-Type', 'text/html');
                        updateRequest.responseType = 'json';
                        updateRequest.onload = () => {
                            if (updateRequest.status === 200) {
                                resolve({
                                    ...updateRequest.response,
                                    account: account,
                                    provider: AuthProvider.GOOGLE,
                                });
                            } else {
                                reject(new Error(`Update failed with status: ${updateRequest.status}`));
                            }
                        };
                        updateRequest.onerror = () => {
                            reject(new Error('Network error occurred during update'));
                        };
                        updateRequest.send(htmlContent);
                    } else {
                        reject(new Error(`Creation failed with status: ${createRequest.status}`));
                    }
                };
                createRequest.onerror = () => {
                    reject(new Error('Network error occurred during creation'));
                };
                createRequest.send(JSON.stringify(metadata));
            });
        } else if (mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
            const metadata = {
                name: fileName,
                mimeType: 'application/vnd.google-apps.spreadsheet',
            };
            const createRequest = new XMLHttpRequest();
            createRequest.open('POST', 'https://www.googleapis.com/drive/v3/files');
            createRequest.setRequestHeader('Authorization', 'Bearer ' + accessToken);
            createRequest.setRequestHeader('Content-Type', 'application/json');
            createRequest.responseType = 'json';
            return new Promise((resolve, reject) => {
                createRequest.onload = async () => {
                    if (createRequest.status === 200) {
                        const fileId = createRequest.response.id;
                        let fileBlob;
                        if (fileContent instanceof ArrayBuffer) {
                            fileBlob = new Blob([new Uint8Array(fileContent)], { type: mimeType });
                        } else if (typeof fileContent === 'string') {
                            fileBlob = new Blob([fileContent], { type: mimeType });
                        } else {
                            fileBlob = fileContent;
                        }
                        const updateRequest = new XMLHttpRequest();
                        updateRequest.open('PATCH', `https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media`);
                        updateRequest.setRequestHeader('Authorization', 'Bearer ' + accessToken);
                        updateRequest.setRequestHeader('Content-Type', mimeType);
                        updateRequest.responseType = 'json';
                        updateRequest.onload = () => {
                            if (updateRequest.status === 200) {
                                resolve({
                                    ...updateRequest.response,
                                    account: account,
                                    provider: AuthProvider.GOOGLE,
                                });
                            } else {
                                reject(new Error(`Update failed with status: ${updateRequest.status}`));
                            }
                        };
                        updateRequest.onerror = () => {
                            reject(new Error('Network error occurred during update'));
                        };
                        updateRequest.send(fileBlob);
                    } else {
                        reject(new Error(`Creation failed with status: ${createRequest.status}`));
                    }
                };
                createRequest.onerror = () => {
                    reject(new Error('Network error occurred during creation'));
                };
                createRequest.send(JSON.stringify(metadata));
            });
        } else if (mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') {
            const metadata = {
                name: fileName,
                mimeType: 'application/vnd.google-apps.presentation',
            };
            const createRequest = new XMLHttpRequest();
            createRequest.open('POST', 'https://www.googleapis.com/drive/v3/files');
            createRequest.setRequestHeader('Authorization', 'Bearer ' + accessToken);
            createRequest.setRequestHeader('Content-Type', 'application/json');
            createRequest.responseType = 'json';
            return new Promise((resolve, reject) => {
                createRequest.onload = async () => {
                    if (createRequest.status === 200) {
                        const fileId = createRequest.response.id;
                        let fileBlob;
                        if (fileContent instanceof ArrayBuffer) {
                            fileBlob = new Blob([new Uint8Array(fileContent)], { type: mimeType });
                        } else if (typeof fileContent === 'string') {
                            fileBlob = new Blob([fileContent], { type: mimeType });
                        } else {
                            fileBlob = fileContent;
                        }
                        const updateRequest = new XMLHttpRequest();
                        updateRequest.open('PATCH', `https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media`);
                        updateRequest.setRequestHeader('Authorization', 'Bearer ' + accessToken);
                        updateRequest.setRequestHeader('Content-Type', mimeType);
                        updateRequest.responseType = 'json';
                        updateRequest.onload = () => {
                            if (updateRequest.status === 200) {
                                resolve({
                                    ...updateRequest.response,
                                    account: account,
                                    provider: AuthProvider.GOOGLE,
                                });
                            } else {
                                reject(new Error(`Update failed with status: ${updateRequest.status}`));
                            }
                        };
                        updateRequest.onerror = () => {
                            reject(new Error('Network error occurred during update'));
                        };
                        updateRequest.send(fileBlob);
                    } else {
                        reject(new Error(`Creation failed with status: ${createRequest.status}`));
                    }
                };
                createRequest.onerror = () => {
                    reject(new Error('Network error occurred during creation'));
                };
                createRequest.send(JSON.stringify(metadata));
            });
        } else {
            const metadata = {
                name: fileName,
                mimeType: mimeType,
            };
            let fileBlob;
            if (fileContent instanceof ArrayBuffer) {
                fileBlob = new Blob([new Uint8Array(fileContent)], { type: mimeType });
            } else if (typeof fileContent === 'string') {
                fileBlob = new Blob([fileContent], { type: mimeType });
            } else {
                fileBlob = fileContent;
            }
            const form = new FormData();
            form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
            form.append('file', fileBlob);
            return new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open('POST', 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,mimeType');
                xhr.setRequestHeader('Authorization', 'Bearer ' + accessToken);
                xhr.responseType = 'json';
                xhr.onload = () => {
                    if (xhr.status === 200) {
                        resolve({
                            ...xhr.response,
                            account: account,
                            provider: AuthProvider.GOOGLE,
                        });
                    } else {
                        reject(new Error(`Upload failed with status: ${xhr.status}`));
                    }
                };
                xhr.onerror = () => {
                    reject(new Error('Network error occurred during upload'));
                };
                xhr.send(form);
            });
        }
    } catch (error) {
        setError && setError(`Failed to create file: ${error.message}`);
        return null;
    } finally {
        setLoading && setLoading(false);
    }
};

async function validateGoogleAccount(account, setError) {
    if (window.gapi && window.gapi.auth2) {
        const authInstance = window.gapi.auth2.getAuthInstance();
        if (authInstance) {
            const currentUser = authInstance.currentUser.get();
            if (currentUser.isSignedIn()) {
                const profile = currentUser.getBasicProfile();
                const currentEmail = profile.getEmail();
                let accountEmail;

                if (typeof account === 'string') {
                    const accountObj = authManagerInstance.getAccounts(AuthProvider.GOOGLE)
                        .find(acc => acc.id === account);
                    accountEmail = accountObj?.userData?.email;
                } else if (account.userData) {
                    accountEmail = account.userData.email;
                } else if (account.email) {
                    accountEmail = account.email;
                }

                if (accountEmail && currentEmail !== accountEmail) {
                    const errorMsg = `Please sign in with ${accountEmail} to access files. Current account: ${currentEmail}`;
                    setError && setError(errorMsg);
                    throw new Error(errorMsg);
                }
            } else {
                throw new Error("No Google user is signed in");
            }
        }
    }
};